import { useEffect, useRef, useState } from 'react';
import { RefreshCw } from 'lucide-react';
import { triggerAppRefresh } from '../hooks/useAppRefresh';

const PULL_THRESHOLD = 80;
const MAX_PULL = 120;
// How long to show the spinner after a refresh is triggered. Pages that
// opt into useAppRefresh will re-fetch during this window.
const SPINNER_HOLD_MS = 600;

export default function PullToRefresh() {
  const [pullDistance, setPullDistance] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const startY = useRef(0);
  const isPulling = useRef(false);
  const pullDistanceRef = useRef(0);

  useEffect(() => {
    const handleTouchStart = (e: TouchEvent) => {
      if (window.scrollY === 0) {
        startY.current = e.touches[0].clientY;
        isPulling.current = true;
      }
    };

    const handleTouchMove = (e: TouchEvent) => {
      if (!isPulling.current) return;
      const delta = e.touches[0].clientY - startY.current;
      if (delta > 0) {
        e.preventDefault();
        const clamped = Math.min(delta, MAX_PULL);
        pullDistanceRef.current = clamped;
        setPullDistance(clamped);
      } else {
        isPulling.current = false;
        pullDistanceRef.current = 0;
        setPullDistance(0);
      }
    };

    const handleTouchEnd = () => {
      if (!isPulling.current) return;
      isPulling.current = false;
      if (pullDistanceRef.current >= PULL_THRESHOLD) {
        setRefreshing(true);
        // Dispatch the refresh event — subscribed pages re-fetch their data.
        // Avoids a full window.location.reload() which otherwise re-parses the
        // JS bundle, re-mounts the React tree, and forces a 3-round-trip auth
        // bootstrap on every pull.
        triggerAppRefresh();
        window.setTimeout(() => {
          setRefreshing(false);
          pullDistanceRef.current = 0;
          setPullDistance(0);
        }, SPINNER_HOLD_MS);
      } else {
        pullDistanceRef.current = 0;
        setPullDistance(0);
      }
    };

    document.addEventListener('touchstart', handleTouchStart, { passive: true });
    document.addEventListener('touchmove', handleTouchMove, { passive: false });
    document.addEventListener('touchend', handleTouchEnd);

    return () => {
      document.removeEventListener('touchstart', handleTouchStart);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleTouchEnd);
    };
  }, []);

  if (pullDistance === 0 && !refreshing) return null;

  const progress = Math.min(pullDistance / PULL_THRESHOLD, 1);
  const isTriggered = pullDistance >= PULL_THRESHOLD || refreshing;

  return (
    <div
      className="fixed top-0 left-0 right-0 z-50 flex items-center justify-center pointer-events-none"
      style={{ height: refreshing ? `${PULL_THRESHOLD}px` : `${pullDistance}px` }}
    >
      <div
        className={`rounded-full bg-white shadow-lg p-2 transition-colors ${isTriggered ? 'text-primary-600' : 'text-neutral-400'}`}
        style={{ opacity: progress || 1 }}
      >
        <RefreshCw
          className={`h-5 w-5 ${refreshing ? 'animate-spin' : ''}`}
          style={{ transform: refreshing ? undefined : `rotate(${progress * 270}deg)` }}
        />
      </div>
    </div>
  );
}
